import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { lazy, Suspense } from "react";
import AdminLoadingSkeleton from "@/components/admin/AdminLoadingSkeleton";

// Lazy-loaded landing page
const Index = lazy(() => import("./pages/Index"));

// Lazy-loaded public pages
const Reservas = lazy(() => import("./pages/Reservas"));
const Restaurante = lazy(() => import("./pages/Restaurante"));
const RestauranteReserva = lazy(() => import("./pages/RestauranteReserva"));
const Direcciones = lazy(() => import("./pages/Direcciones"));
const Alojamiento = lazy(() => import("./pages/Alojamiento"));
const Encuesta = lazy(() => import("./pages/Encuesta"));
const TerminosCondiciones = lazy(() => import("./pages/TerminosCondiciones"));
const PoliticaPrivacidad = lazy(() => import("./pages/PoliticaPrivacidad"));
const PoliticaCancelacion = lazy(() => import("./pages/PoliticaCancelacion"));
const ConsejosGlamping = lazy(() => import("./pages/ConsejosGlamping"));
const MiReserva = lazy(() => import("./pages/MiReserva"));
const GiftCards = lazy(() => import("./pages/GiftCards"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));

// Lazy-loaded admin pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminHotel = lazy(() => import("./pages/admin/Hotel"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminCoupons = lazy(() => import("./pages/admin/Coupons"));
const AdminCalendarSync = lazy(() => import("./pages/admin/CalendarSync"));
const AdminRestaurante = lazy(() => import("./pages/admin/Restaurante"));
const AdminLeads = lazy(() => import("./pages/admin/Leads"));
const AdminFacturas = lazy(() => import("./pages/admin/Facturas"));
const AdminContabilidad = lazy(() => import("./pages/admin/Contabilidad"));
const GuestAccount = lazy(() => import("./pages/admin/GuestAccount"));
const MiCuenta = lazy(() => import("./pages/admin/MiCuenta"));
const RecepcionDashboard = lazy(() => import("./pages/admin/Recepcion"));
const DeletionLogs = lazy(() => import("./pages/admin/DeletionLogs"));
const ChannelManager = lazy(() => import("./pages/admin/ChannelManager"));
const ReportesComerciales = lazy(() => import("./pages/admin/ReportesComerciales"));
const AdminInventario = lazy(() => import("./pages/admin/Inventario"));
const AdminAnomalias = lazy(() => import("./pages/admin/Anomalias"));
import { AdminLayout } from "./components/admin/AdminLayout";
import RoleGuard from "./components/admin/RoleGuard";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            {/* Note: Admin routes use AdminLoadingSkeleton via RoleGuard */}
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/reservas" element={<Reservas />} />
            <Route path="/alojamiento/:id" element={<Alojamiento />} />
            <Route path="/restaurante" element={<Restaurante />} />
            <Route path="/restaurante/reserva" element={<RestauranteReserva />} />
            <Route path="/encuesta/:reservationId" element={<Encuesta />} />
            <Route path="/como-llegar" element={<Direcciones />} />
            <Route path="/terminos-y-condiciones" element={<TerminosCondiciones />} />
            <Route path="/politica-de-privacidad" element={<PoliticaPrivacidad />} />
            <Route path="/politica-de-cancelacion" element={<PoliticaCancelacion />} />
            <Route path="/mi-reserva" element={<MiReserva />} />
            <Route path="/consejos-para-ir-al-glamping" element={<ConsejosGlamping />} />
            <Route path="/gift-cards" element={<GiftCards />} />
            <Route path="/gift-card" element={<Navigate to="/gift-cards" replace />} />
            <Route path="/api-docs" element={<ApiDocs />} />
            
            {/* 301 Redirects from old Wix URLs */}
            <Route path="/reservations" element={<Navigate to="/reservas" replace />} />
            <Route path="/reservas-del-restaurante" element={<Navigate to="/restaurante" replace />} />
            <Route path="/cómo-llegar" element={<Navigate to="/como-llegar" replace />} />
            <Route path="/c%C3%B3mo-llegar" element={<Navigate to="/como-llegar" replace />} />
            <Route path="/habitaciones-y-precios" element={<Navigate to="/reservas" replace />} />
            <Route path="/blog" element={<Navigate to="/" replace />} />
            <Route path="/actividades" element={<Navigate to="/" replace />} />
            <Route path="/experiences" element={<Navigate to="/" replace />} />
            <Route path="/event-list" element={<Navigate to="/" replace />} />
            <Route path="/event-details" element={<Navigate to="/" replace />} />
            <Route path="/event-details/*" element={<Navigate to="/" replace />} />
            <Route path="/members" element={<Navigate to="/" replace />} />
            <Route path="/members/*" element={<Navigate to="/" replace />} />
            <Route path="/loyalty" element={<Navigate to="/" replace />} />
            <Route path="/shop" element={<Navigate to="/" replace />} />
            <Route path="/shop/*" element={<Navigate to="/" replace />} />
            <Route path="/product-page" element={<Navigate to="/" replace />} />
            <Route path="/product-page/*" element={<Navigate to="/" replace />} />
            <Route path="/post/*" element={<Navigate to="/" replace />} />
            <Route path="/categories/*" element={<Navigate to="/" replace />} />
            <Route path="/consejos-para-viajar-*" element={<Navigate to="/consejos-para-ir-al-glamping" replace />} />
            <Route path="/politica-de-privacidad-wix" element={<Navigate to="/politica-de-privacidad" replace />} />

            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
             <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<RoleGuard allowedRoles={["admin", "gerencia", "recepcion", "rrss", "meseros", "cocina"]}><RecepcionDashboard /></RoleGuard>} />
                <Route path="estadisticas" element={<RoleGuard allowedRoles={["admin", "gerencia"]}><AdminDashboard /></RoleGuard>} />
                <Route path="hotel" element={<RoleGuard allowedRoles={["admin", "gerencia", "recepcion", "rrss", "encuesta"]}><AdminHotel /></RoleGuard>} />
                <Route path="hotel/channel-manager" element={<RoleGuard allowedRoles={["admin"]}><ChannelManager /></RoleGuard>} />
               <Route path="cuenta/:reservationId" element={<RoleGuard allowedRoles={["admin", "gerencia", "recepcion", "rrss"]}><GuestAccount /></RoleGuard>} />
               <Route path="restaurante" element={<RoleGuard allowedRoles={["admin", "gerencia", "recepcion", "rrss", "meseros", "cocina"]}><AdminRestaurante /></RoleGuard>} />
               <Route path="contabilidad" element={<RoleGuard allowedRoles={["admin", "gerencia", "rrss"]}><AdminContabilidad /></RoleGuard>} />
               <Route path="reportes" element={<RoleGuard allowedRoles={["admin", "gerencia"]}><ReportesComerciales /></RoleGuard>} />
               <Route path="inventario" element={<RoleGuard allowedRoles={["admin", "gerencia", "recepcion", "meseros", "cocina", "rrss"]}><AdminInventario /></RoleGuard>} />
               <Route path="anomalias" element={<RoleGuard allowedRoles={["admin"]}><AdminAnomalias /></RoleGuard>} />

               <Route path="facturas" element={<RoleGuard allowedRoles={["admin"]}><AdminFacturas /></RoleGuard>} />
               <Route path="cupones" element={<RoleGuard allowedRoles={["admin", "gerencia"]}><AdminCoupons /></RoleGuard>} />
               <Route path="calendario-sync" element={<RoleGuard allowedRoles={["admin"]}><AdminCalendarSync /></RoleGuard>} />
               <Route path="leads" element={<RoleGuard allowedRoles={["admin"]}><AdminLeads /></RoleGuard>} />
               <Route path="usuarios" element={<RoleGuard allowedRoles={["admin"]}><AdminUsers /></RoleGuard>} />
               <Route path="configuracion" element={<RoleGuard allowedRoles={["admin"]}><AdminSettings /></RoleGuard>} />
               <Route path="mi-cuenta" element={<RoleGuard allowedRoles={["admin", "gerencia", "recepcion", "meseros", "cocina", "limpieza", "rrss"]}><MiCuenta /></RoleGuard>} />
               <Route path="logs" element={<RoleGuard allowedRoles={["admin"]}><DeletionLogs /></RoleGuard>} />
             </Route>
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
